import React from "react";
import { AlertTriangle, Info, AlertCircle } from "lucide-react";
import { cn } from "utils/cn";

const levelConfig = {
  critical: {
    icon: AlertCircle,
    bg: "bg-destructive/10 border-destructive/30",
    text: "text-destructive",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-warning/10 border-warning/30",
    text: "text-warning",
  },
  info: {
    icon: Info,
    bg: "bg-primary/10 border-primary/30",
    text: "text-primary",
  },
};

export default function AlertPanel({ alerts = [] }) {
  const summary = alerts.reduce(
    (acc, alert) => {
      acc[alert.level] = (acc[alert.level] || 0) + 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 },
  );

  return (
    <div className="metric-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-sm">Active Alerts</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {alerts.length} item{alerts.length === 1 ? "" : "s"} requiring attention
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-1">
          {summary.critical > 0 && (
            <span className="inline-flex rounded-full bg-destructive/10 px-2 py-1 text-[11px] font-medium text-destructive">
              {summary.critical} critical
            </span>
          )}
          {summary.warning > 0 && (
            <span className="inline-flex rounded-full bg-warning/10 px-2 py-1 text-[11px] font-medium text-warning">
              {summary.warning} warning
            </span>
          )}
          {summary.info > 0 && (
            <span className="inline-flex rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
              {summary.info} info
            </span>
          )}
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
          No active alerts for this view.
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {alerts.map((alert) => {
            const config = levelConfig[alert.level] || levelConfig.info;
            const Icon = config.icon;

            return (
              <div
                key={alert.id}
                className={cn("flex items-start gap-2 p-2 rounded-md border text-xs", config.bg)}
              >
                <Icon className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", config.text)} />
                <div>
                  <p className="font-medium">{alert.message}</p>
                  <p className="text-muted-foreground">{alert.location}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
